import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

/**
 * Atmospheric video background.
 *
 * - Paints the poster as a real <img>, then fades the video in once it can play.
 * - Autoplays muted/looped/inline (the pattern browsers actually allow).
 * - Honours `prefers-reduced-motion`: poster only, no playback, no video fetch.
 * - `overlay` accepts any CSS gradient/color string layered above the video.
 *
 * WHY THE POSTER IS AN <img> AND NOT THE VIDEO'S `poster` ATTRIBUTE
 * -----------------------------------------------------------------
 * It used to be `<video poster="…">`. A <video> does not paint its poster until
 * the media resource has resolved one way or the other, so the hero's Largest
 * Contentful Paint was chained to a network request for the clip. Measured on
 * throttled mobile that cost 8.3 s — and it was worse than it looks, because
 * `/videos/hair-hero.mp4` is not in the repository (see utils/heroBackground.js)
 * so the request 404s and the poster waited on a failure.
 *
 * Painting the poster as its own <img> decouples the two: the image is a normal
 * high-priority resource that renders as soon as it arrives, and the video
 * layers over it later. This helps whether or not the clip exists — once real
 * footage ships, a multi-megabyte download will no longer hold up LCP either.
 *
 * `priority` marks the one instance that is above the fold (the homepage hero),
 * so only that poster claims high fetch priority; the rest stay lazy.
 */
const VideoBackground = ({
    src,
    poster,
    overlay = "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.7) 100%)",
    objectPosition = "center",
    priority = false,
    sx,
    children,
}) => {
    const videoRef = useRef(null);
    const [canPlay, setCanPlay] = useState(false);
    const [reduced, setReduced] = useState(false);

    useEffect(() => {
        const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        if (prefersReduced) {
            setReduced(true);
            return;
        }

        const node = videoRef.current;
        if (!node) return;

        // Some browsers (mobile Safari especially) need an explicit play() call
        // even with the autoplay attribute set. Failures are safe — the poster
        // image remains visible underneath.
        const p = node.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
    }, []);

    return (
        <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", ...sx }}>
            {/* Poster — the LCP candidate. Always painted, never waits on video. */}
            {poster && (
                <Box
                    component="img"
                    src={poster}
                    alt=""
                    aria-hidden="true"
                    fetchpriority={priority ? "high" : "auto"}
                    loading={priority ? "eager" : "lazy"}
                    decoding="async"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition,
                        pointerEvents: "none",
                    }}
                />
            )}

            {/* Video — fades over the poster once it has enough data. Skipped
                entirely under reduced motion, which also saves the download. */}
            {!reduced && src && (
                <Box
                    component="video"
                    ref={videoRef}
                    src={src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    onCanPlay={() => setCanPlay(true)}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition,
                        pointerEvents: "none",
                        opacity: canPlay ? 1 : 0,
                        transition: "opacity 0.6s ease-out",
                    }}
                />
            )}

            {overlay && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: overlay,
                        pointerEvents: "none",
                    }}
                />
            )}
            {children}
        </Box>
    );
};

export default VideoBackground;
